import { Centered, FullScreenContainer } from '@gorila-shared-ui/components';
import { useState } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import {
  ACCOUNT_PAGE,
  ALARMS_REPORTS_PAGE,
  COMMANDS_PAGE,
  CONSUMPTION_REPORTS_PAGE,
  CONTROL_TOWER_PAGE,
  CONVOYS_PAGE,
  DASHBOARD_HOME_ROUTE,
  DASHBOARD_HOME_ROUTE_ID,
  DASHBOARD_ROUTES_SECTION_ID,
  DELIVERY_GEOFENCES_PAGE,
  DELIVERY_REPORTS_PAGE,
  DOOR_REPORTS_PAGE,
  DOWNLOADS_PAGE,
  EVIDENCE_REPORT_DETAIL_PAGE,
  EVIDENCES_REPORTS_PAGE,
  GEOFENCES_PAGE,
  GPT_DETAIL_PAGE,
  GROUPS_PAGE,
  JOURNEYS_PAGE,
  LINKS_PAGE,
  LIVE_PAGE,
  LIVE_STATUS_PAGE,
  NOTIFICATION_EVIDENCE_DETAIL_PAGE,
  NOTIFICATION_EVIDENCES_PAGE,
  NOTIFICATION_PAGE,
  PATHS_REPORTS_PAGE,
  PLACES_PAGE,
  PUBLIC_LIVE_PAGE,
  PUBLIC_ROUTES_SECTION_ID,
  REPORTS_PAGE,
  REQUEST_VIDEO_PAGE,
  ROUTE_REPORTS_PAGE,
  SECONDARIES_PAGE,
  SETTINGS_PAGE,
  STREAM_PAGE,
  TELEMETRY_REPORTS_PAGE,
  TRACKING_PAGE,
} from '../../constants/app';
import {
  FLEET_ASSETS_PAGE,
  FLEET_DOWNLOADS_PAGE,
  FLEET_DRIVER_PAGE,
  FLEET_DRIVERS_PAGE,
  FLEET_HOME_ROUTE,
  FLEET_HOME_ROUTE_ID,
  FLEET_MAINTENANCE_TYPES_PAGE,
  FLEET_OPERATIONAL_REPORTS_PAGE,
  FLEET_REPORTS_PAGE,
  FLEET_ROUTES_SECTION_ID,
  FLEET_RULES_PAGE,
  FLEET_SERVICE_REPORTS_PAGE,
  FLEET_SERVICES_PAGE,
} from '../../constants/fleet';
import useEffectOnce from '../../hooks/useEffectOnce';
import { useStyles } from '../../hooks/useStyles';
import { notificationSelectedState } from '../../storage/notifications';
import { reportSelectedAssetIdsState } from '../../storage/reports';
import { Dashboard } from '../layout/Dashboard';
import { FleetDashboard } from '../layout/FleetDashboard';
import PublicDashboard from '../layout/public/PublicDashboard';
import { AccountPage } from '../pages/account/AccountPage';
import { CommandsPage } from '../pages/CommandsPage';
import { ControlTowerPage } from '../pages/ControlTowerPage';
import { ConvoysPage } from '../pages/ConvoysPage';
import { DeliveryFencesPage } from '../pages/DeliveryFencesPage';
import { DownloadsPage } from '../pages/DownloadsPage';
import EvidenceMediaDetailPage from '../pages/EvidenceMediaDetailPage';
import { FencesPage } from '../pages/FencesPage';
import FleetAssetsPage from '../pages/fleet/FleetAssetsPage';
import FleetDownloadsPage from '../pages/fleet/FleetDownloadsPage';
import FleetDriverPage from '../pages/fleet/FleetDriverPage';
import FleetDriversPage from '../pages/fleet/FleetDriversPage';
import FleetMaintenanceTypesPage from '../pages/fleet/FleetMaintenanceTypesPage';
import FleetOperationalReportsPage from '../pages/fleet/FleetOperationalReportsPage';
import FleetReportsPage from '../pages/fleet/FleetReportsPage';
import FleetRulesPage from '../pages/fleet/FleetRulesPage';
import FleetServiceReportsPage from '../pages/fleet/FleetServiceReportsPage';
import FleetServicesPage from '../pages/fleet/FleetServicesPage';
import { ForgottenPasswordPage } from '../pages/ForgottenPasswordPage';
import { GptEventPage } from '../pages/GptEventPage';
import { GroupPage } from '../pages/GroupPage';
import { JourneysPage } from '../pages/JourneysPage';
import { LinksPage } from '../pages/LinksPage';
import LivePage from '../pages/live/LivePage';
import RequestVideoPage from '../pages/live/RequestVideoPage';
import StreamPage from '../pages/live/StreamPage';
import { LiveStatusPage } from '../pages/LiveStatusPage';
import { LoginPage } from '../pages/LoginPage';
import NotificationEvidencesPage from '../pages/notifications/NotificationEvidencesPage';
import NotificationPage from '../pages/notifications/NotificationPage';
import { PlacesPage } from '../pages/PlacesPage';
import PublicLivePage from '../pages/public/PublicLivePage';
import AlarmsReportsPage from '../pages/reports/AlarmsReportsPage';
import ConsumptionReportsPage from '../pages/reports/ConsumptionReportsPage';
import DeliveryReportsPage from '../pages/reports/DeliveryReportsPage';
import DoorReportsPage from '../pages/reports/DoorReportsPage';
import EvidencesReportsPage from '../pages/reports/EvidencesReportsPage';
import PathsReportsPage from '../pages/reports/PathsReportsPage';
import RouteReportsPage from '../pages/reports/RouteReportsPage';
import TelemetryReportsPage from '../pages/reports/TelemetryReportsPage';
import { ReportsPage } from '../pages/ReportsPage';
import { SecondariesPage } from '../pages/SecondariesPage';
import { ServerErrorPage } from '../pages/ServerErrorPage';
import { SettingsPage } from '../pages/SettingsPage';
import { TrackingPage } from '../pages/TrackingPage';
import ZEProvider from '../providers/ZEProvider';
import ReportFiltersProvider from '../reports/ReportFiltersProvider';
import { ErrorElementBoundary } from '../shared/ErrorBoundary';
import { WorkspacePicker } from '../workspace/WorkspacePicker';
import PermissionRoute from './PermissionRoute';

const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <ErrorElementBoundary />,
  },
  {
    path: '/forgotten-password',
    element: <ForgottenPasswordPage />,
    errorElement: <ErrorElementBoundary />,
  },
  {
    id: PUBLIC_ROUTES_SECTION_ID,
    element: <PublicDashboard />,
    errorElement: <ErrorElementBoundary />,
    children: [
      {
        id: PUBLIC_LIVE_PAGE.id,
        path: PUBLIC_LIVE_PAGE.route,
        element: <PublicLivePage />,
      },
    ],
  },
  {
    id: DASHBOARD_ROUTES_SECTION_ID,
    element: <Dashboard />,
    errorElement: <ErrorElementBoundary />,
    children: [
      {
        id: LIVE_PAGE.id,
        path: LIVE_PAGE.route,
        element: (
          <PermissionRoute permissionName={LIVE_PAGE.permissionName}>
            <LivePage />
          </PermissionRoute>
        ),
      },
      {
        id: REQUEST_VIDEO_PAGE.id,
        path: REQUEST_VIDEO_PAGE.route,
        element: (
          <PermissionRoute permissionName={REQUEST_VIDEO_PAGE.permissionName}>
            <RequestVideoPage />
          </PermissionRoute>
        ),
      },
      {
        id: STREAM_PAGE.id,
        path: STREAM_PAGE.route,
        element: (
          <PermissionRoute permissionName={STREAM_PAGE.permissionName}>
            <StreamPage />
          </PermissionRoute>
        ),
      },
      {
        id: GEOFENCES_PAGE.id,
        path: GEOFENCES_PAGE.route,
        element: (
          <PermissionRoute permissionName={GEOFENCES_PAGE.permissionName}>
            <FencesPage />
          </PermissionRoute>
        ),
      },
      {
        id: DELIVERY_GEOFENCES_PAGE.id,
        path: DELIVERY_GEOFENCES_PAGE.route,
        element: (
          <PermissionRoute permissionName={DELIVERY_GEOFENCES_PAGE.permissionName}>
            <DeliveryFencesPage />
          </PermissionRoute>
        ),
      },
      {
        id: COMMANDS_PAGE.id,
        path: COMMANDS_PAGE.route,
        element: (
          <PermissionRoute permissionName={COMMANDS_PAGE.permissionName}>
            <CommandsPage />
          </PermissionRoute>
        ),
      },
      {
        id: GROUPS_PAGE.id,
        path: GROUPS_PAGE.route,
        element: (
          <PermissionRoute permissionName={GROUPS_PAGE.permissionName}>
            <GroupPage />
          </PermissionRoute>
        ),
      },
      {
        id: CONTROL_TOWER_PAGE.id,
        path: CONTROL_TOWER_PAGE.route,
        element: (
          <PermissionRoute permissionName={CONTROL_TOWER_PAGE.permissionName}>
            <ControlTowerPage />
          </PermissionRoute>
        ),
      },
      {
        id: GPT_DETAIL_PAGE.id,
        path: GPT_DETAIL_PAGE.route,
        element: (
          <PermissionRoute permissionName={CONTROL_TOWER_PAGE.permissionName}>
            <GptEventPage />
          </PermissionRoute>
        ),
      },
      {
        id: JOURNEYS_PAGE.id,
        path: JOURNEYS_PAGE.route,
        element: (
          <PermissionRoute permissionName={JOURNEYS_PAGE.permissionName}>
            <JourneysPage />
          </PermissionRoute>
        ),
      },
      {
        id: 'reportsProvider',
        element: (
          <PermissionRoute permissionName={REPORTS_PAGE.permissionName}>
            <ReportFiltersProvider />
          </PermissionRoute>
        ),
        children: [
          {
            id: REPORTS_PAGE.id,
            path: REPORTS_PAGE.route,
            element: <ReportsPage />,
          },
          {
            id: ROUTE_REPORTS_PAGE.id,
            path: ROUTE_REPORTS_PAGE.route,
            element: <RouteReportsPage />,
          },
          {
            id: PATHS_REPORTS_PAGE.id,
            path: PATHS_REPORTS_PAGE.route,
            element: <PathsReportsPage />,
          },
          {
            id: ALARMS_REPORTS_PAGE.id,
            path: ALARMS_REPORTS_PAGE.route,
            element: <AlarmsReportsPage />,
          },
          {
            id: EVIDENCES_REPORTS_PAGE.id,
            path: EVIDENCES_REPORTS_PAGE.route,
            element: <EvidencesReportsPage />,
          },
          {
            id: EVIDENCE_REPORT_DETAIL_PAGE.id,
            path: EVIDENCE_REPORT_DETAIL_PAGE.route,
            element: (
              <EvidenceMediaDetailPage
                redirectRoute={EVIDENCES_REPORTS_PAGE.route}
                fromExternalRedirectRoute={REPORTS_PAGE.route}
                internalState={reportSelectedAssetIdsState}
              />
            ),
          },
          {
            id: TELEMETRY_REPORTS_PAGE.id,
            path: TELEMETRY_REPORTS_PAGE.route,
            element: <TelemetryReportsPage />,
          },
          {
            id: CONSUMPTION_REPORTS_PAGE.id,
            path: CONSUMPTION_REPORTS_PAGE.route,
            element: <ConsumptionReportsPage />,
          },
          {
            id: DOOR_REPORTS_PAGE.id,
            path: DOOR_REPORTS_PAGE.route,
            element: <DoorReportsPage />,
          },
          {
            id: DELIVERY_REPORTS_PAGE.id,
            path: DELIVERY_REPORTS_PAGE.route,
            element: <DeliveryReportsPage />,
          },
          {
            id: NOTIFICATION_EVIDENCES_PAGE.id,
            path: NOTIFICATION_EVIDENCES_PAGE.route,
            element: <NotificationEvidencesPage />,
          },
          {
            id: NOTIFICATION_EVIDENCE_DETAIL_PAGE.id,
            path: NOTIFICATION_EVIDENCE_DETAIL_PAGE.route,
            element: (
              <EvidenceMediaDetailPage
                redirectRoute={NOTIFICATION_EVIDENCES_PAGE.route}
                fromExternalRedirectRoute={LIVE_PAGE.route}
                internalState={notificationSelectedState}
              />
            ),
          },
        ],
      },
      {
        id: DOWNLOADS_PAGE.id,
        path: DOWNLOADS_PAGE.route,
        element: (
          <PermissionRoute permissionName={REPORTS_PAGE.permissionName}>
            <DownloadsPage />
          </PermissionRoute>
        ),
      },
      {
        id: SETTINGS_PAGE.id,
        path: SETTINGS_PAGE.route,
        element: (
          <PermissionRoute permissionName={SETTINGS_PAGE.permissionName}>
            <SettingsPage />
          </PermissionRoute>
        ),
      },
      {
        id: ACCOUNT_PAGE.id,
        path: ACCOUNT_PAGE.route,
        element: (
          <PermissionRoute permissionName={ACCOUNT_PAGE.permissionName}>
            <AccountPage />
          </PermissionRoute>
        ),
      },
      {
        id: PLACES_PAGE.id,
        path: PLACES_PAGE.route,
        element: (
          <PermissionRoute permissionName={SETTINGS_PAGE.permissionName}>
            <PlacesPage />
          </PermissionRoute>
        ),
      },
      {
        id: TRACKING_PAGE.id,
        path: TRACKING_PAGE.route,
        element: (
          <PermissionRoute permissionName={TRACKING_PAGE.permissionName}>
            <TrackingPage />
          </PermissionRoute>
        ),
      },
      {
        id: SECONDARIES_PAGE.id,
        path: SECONDARIES_PAGE.route,
        element: (
          <PermissionRoute permissionName={SECONDARIES_PAGE.permissionName}>
            <SecondariesPage />
          </PermissionRoute>
        ),
      },
      {
        id: NOTIFICATION_PAGE.id,
        path: NOTIFICATION_PAGE.route,
        element: (
          <PermissionRoute permissionName={NOTIFICATION_PAGE.permissionName}>
            <NotificationPage />
          </PermissionRoute>
        ),
      },
      {
        id: LIVE_STATUS_PAGE.id,
        path: LIVE_STATUS_PAGE.route,
        element: (
          <PermissionRoute permissionName={LIVE_STATUS_PAGE.permissionName}>
            <LiveStatusPage />
          </PermissionRoute>
        ),
      },
      {
        id: CONVOYS_PAGE.id,
        path: CONVOYS_PAGE.route,
        element: (
          <PermissionRoute permissionName={CONVOYS_PAGE.permissionName}>
            <ConvoysPage />
          </PermissionRoute>
        ),
      },
      {
        id: LINKS_PAGE.id,
        path: LINKS_PAGE.route,
        element: (
          <PermissionRoute permissionName={LINKS_PAGE.permissionName}>
            <LinksPage />
          </PermissionRoute>
        ),
      },
      {
        id: DASHBOARD_HOME_ROUTE_ID,
        path: DASHBOARD_HOME_ROUTE,
        element: <WorkspacePicker />,
      },
    ],
  },
  {
    id: FLEET_ROUTES_SECTION_ID,
    element: <FleetDashboard />,
    errorElement: <ErrorElementBoundary />,
    children: [
      {
        id: FLEET_ASSETS_PAGE.id,
        path: FLEET_ASSETS_PAGE.route,
        element: (
          <PermissionRoute permissionName={FLEET_ASSETS_PAGE.permissionName}>
            <FleetAssetsPage />
          </PermissionRoute>
        ),
      },
      {
        id: FLEET_DRIVERS_PAGE.id,
        path: FLEET_DRIVERS_PAGE.route,
        element: (
          <PermissionRoute permissionName={FLEET_DRIVERS_PAGE.permissionName}>
            <FleetDriversPage />
          </PermissionRoute>
        ),
      },
      {
        id: FLEET_DRIVER_PAGE.id,
        path: FLEET_DRIVER_PAGE.route,
        element: (
          <PermissionRoute permissionName={FLEET_DRIVER_PAGE.permissionName}>
            <FleetDriverPage />
          </PermissionRoute>
        ),
      },
      {
        id: FLEET_SERVICES_PAGE.id,
        path: FLEET_SERVICES_PAGE.route,
        element: (
          <PermissionRoute permissionName={FLEET_SERVICES_PAGE.permissionName}>
            <FleetServicesPage />
          </PermissionRoute>
        ),
      },
      {
        id: FLEET_RULES_PAGE.id,
        path: FLEET_RULES_PAGE.route,
        element: (
          <PermissionRoute permissionName={FLEET_RULES_PAGE.permissionName}>
            <FleetRulesPage />
          </PermissionRoute>
        ),
      },
      {
        id: FLEET_MAINTENANCE_TYPES_PAGE.id,
        path: FLEET_MAINTENANCE_TYPES_PAGE.route,
        element: (
          <PermissionRoute permissionName={FLEET_MAINTENANCE_TYPES_PAGE.permissionName}>
            <FleetMaintenanceTypesPage />
          </PermissionRoute>
        ),
      },
      {
        id: FLEET_REPORTS_PAGE.id,
        path: FLEET_REPORTS_PAGE.route,
        element: (
          <PermissionRoute permissionName={FLEET_REPORTS_PAGE.permissionName}>
            <FleetReportsPage />
          </PermissionRoute>
        ),
      },
      {
        id: FLEET_SERVICE_REPORTS_PAGE.id,
        path: FLEET_SERVICE_REPORTS_PAGE.route,
        element: (
          <PermissionRoute permissionName={FLEET_SERVICE_REPORTS_PAGE.permissionName}>
            <FleetServiceReportsPage />
          </PermissionRoute>
        ),
      },
      {
        id: FLEET_OPERATIONAL_REPORTS_PAGE.id,
        path: FLEET_OPERATIONAL_REPORTS_PAGE.route,
        element: (
          <PermissionRoute permissionName={FLEET_OPERATIONAL_REPORTS_PAGE.permissionName}>
            <FleetOperationalReportsPage />
          </PermissionRoute>
        ),
      },
      {
        id: FLEET_DOWNLOADS_PAGE.id,
        path: FLEET_DOWNLOADS_PAGE.route,
        element: (
          <PermissionRoute permissionName={FLEET_DOWNLOADS_PAGE.permissionName}>
            <FleetDownloadsPage />
          </PermissionRoute>
        ),
      },
      {
        id: FLEET_HOME_ROUTE_ID,
        path: FLEET_HOME_ROUTE,
        element: <WorkspacePicker />,
      },
    ],
  },
  {
    id: '404',
    path: '/*',
    element: <Centered>404</Centered>,
  },
  {
    id: '500',
    path: '/500',
    element: <ServerErrorPage />,
  },
]);

export function AppRoutes() {
  const { classes, theme } = useStyles();
  const [loading, setLoading] = useState(true);

  useEffectOnce(() => {
    document.fonts.ready.then(() => {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    });
  });

  return (
    <FullScreenContainer>
      <RouterProvider router={router} />
      {loading && (
        <div
          className={`${classes.verticalFlex} ${classes.centeredFlex}`}
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            background: theme.colors.backgroundInversePrimary,
          }}
        >
          <img
            alt="Gorila MX"
            src="/img/login_logo.png"
            height={50}
            style={{
              animation: 'pulse 2s infinite',
            }}
          />
        </div>
      )}
      <ZEProvider />
    </FullScreenContainer>
  );
}
